import { FormEvent, ReactNode, useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../lib/branding";
import { PRIVACY_URL } from "../lib/links";

/**
 * The account selector lists every demo role, partner included. Choosing one
 * fills the credentials — it does not *grant* the role. Role always comes from
 * the account record on the server; a dropdown that decided your access level
 * would be a security hole wearing a convenience costume. In production the
 * selector simply is not rendered.
 */
const DEMO_ACCOUNTS = [
    { email: "admin@trustcaresupport.com.au", label: "Admin" },
    { email: "manager@trustcare.test", label: "Manager" },
    { email: "supervisor@trustcare.test", label: "Supervisor" },
    { email: "staff@trustcare.test", label: "Staff" },
    { email: "viewer@trustcare.test", label: "Viewer (CRM read-only)" },
    { email: "partner@pds.test", label: "Partner (provider organisation)" },
];

const DEMO_PASSWORD = "TrustCare2026!";
const SHOW_DEMO = import.meta.env.DEV;

/** Fallback support inbox when the instance has no branding.supportEmail set. */
const DEFAULT_SUPPORT_EMAIL = "ict@trustcaresupport.com.au";

const Icon = ({ children }: { children: ReactNode }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);

export const Login = () => {
    const { user, signIn } = useAuth();
    const branding = useBranding();
    const [email, setEmail] = useState(SHOW_DEMO ? "manager@trustcare.test" : "");
    const [password, setPassword] = useState(SHOW_DEMO ? DEMO_PASSWORD : "");
    const [selected, setSelected] = useState("manager@trustcare.test");
    const [showPw, setShowPw] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    // 3D pointer-tilt (skipped for touch / reduced-motion — see effect below)
    const tiltRef = useRef<HTMLDivElement>(null);
    const cardRef = useRef<HTMLDivElement>(null);

    // Contact-support modal
    const [supportOpen, setSupportOpen] = useState(false);
    const [csEmail, setCsEmail] = useState("");
    const [csMsg, setCsMsg] = useState("");
    const openBtnRef = useRef<HTMLButtonElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const csEmailRef = useRef<HTMLInputElement>(null);
    const supportEmail = branding.supportEmail ?? DEFAULT_SUPPORT_EMAIL;

    useEffect(() => {
        const tilt = tiltRef.current, card = cardRef.current;
        if (!tilt || !card) return;
        const reduce = window.matchMedia("(prefers-reduced-motion:reduce)").matches;
        const coarse = window.matchMedia("(pointer:coarse)").matches;
        if (reduce || coarse) return;
        let raf = 0;
        const move = (e: PointerEvent) => {
            if (raf) return;
            raf = requestAnimationFrame(() => {
                raf = 0;
                const r = tilt.getBoundingClientRect();
                const px = (e.clientX - r.left) / r.width - 0.5;
                const py = (e.clientY - r.top) / r.height - 0.5;
                card.style.transform = `rotateY(${(px * 5).toFixed(2)}deg) rotateX(${(-py * 5).toFixed(2)}deg) translateY(-2px)`;
            });
        };
        const leave = () => { card.style.transform = ""; };
        tilt.addEventListener("pointermove", move);
        tilt.addEventListener("pointerleave", leave);
        return () => { tilt.removeEventListener("pointermove", move); tilt.removeEventListener("pointerleave", leave); };
    }, []);

    // Modal: focus management, Esc + focus trap, body scroll lock
    useEffect(() => {
        if (!supportOpen) return;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const focusTimer = window.setTimeout(() => csEmailRef.current?.focus(), 30);
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") { e.preventDefault(); setSupportOpen(false); return; }
            if (e.key === "Tab" && modalRef.current) {
                const f = Array.from(modalRef.current.querySelectorAll<HTMLElement>(
                    'button,[href],input,textarea,select,[tabindex]:not([tabindex="-1"])'
                )).filter((el) => !(el as HTMLButtonElement).disabled && el.offsetParent !== null);
                if (!f.length) return;
                const first = f[0], last = f[f.length - 1];
                if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
                else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
            }
        };
        document.addEventListener("keydown", onKey);
        return () => {
            window.clearTimeout(focusTimer);
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prevOverflow;
            openBtnRef.current?.focus();
        };
    }, [supportOpen]);

    if (user) {
        return <Navigate to="/" replace />;
    }

    const pick = (accountEmail: string) => {
        setSelected(accountEmail);
        setEmail(accountEmail);
        setPassword(DEMO_PASSWORD);
    };

    const submit = async (event: FormEvent) => {
        event.preventDefault();
        setBusy(true);
        setError(null);
        try {
            await signIn(email, password);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Sign-in failed");
        } finally {
            setBusy(false);
        }
    };

    const sendSupport = (event: FormEvent) => {
        event.preventDefault();
        if (!csEmail.trim() || !csMsg.trim()) {
            (!csEmail.trim() ? csEmailRef.current : null)?.focus();
            return;
        }
        const subject = "CRM sign-in help request";
        const body =
            `From: ${csEmail.trim()}\n\nIssue:\n${csMsg.trim()}\n\n— Sent from the ${branding.orgName} sign-in page`;
        window.location.href =
            `mailto:${supportEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        setSupportOpen(false);
    };

    return (
        <div className="tcl-page">

            {/* Brand hero */}
            <section className="tcl-brand" aria-label={branding.orgName}>
                <img className="tcl-brand-logo" src="/brand/logo-horizontal-white.png"
                     alt={branding.orgName} />
                <div className="tcl-brand-copy">
                    <span className="tcl-accent" aria-hidden="true" />
                    <h2>Reliable clinical care, every shift.</h2>
                    <p>The clinical nursing partner for registered NDIS providers — consistency, accountability and respect.</p>
                </div>
            </section>

            {/* Sign in */}
            <main className="tcl-auth">
                <div className="tcl-tilt" ref={tiltRef}>
                    <div className="tcl-card" ref={cardRef}>
                        <span className="tcl-eyebrow">Secure Access</span>
                        <h1>Sign in</h1>

                        <form className="tcl-form" onSubmit={submit} noValidate>
                            {error && <div className="tcl-error">{error}</div>}

                            <div className="tcl-field">
                                <label htmlFor="email">Email address</label>
                                <div className="tcl-control">
                                    <span className="tcl-lead">
                                        <Icon><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></Icon>
                                    </span>
                                    <input id="email" type="email" value={email}
                                           onChange={(e) => setEmail(e.target.value)}
                                           placeholder="you@trustcare.com.au" autoComplete="username" required />
                                </div>
                            </div>

                            <div className="tcl-field tcl-field--pw">
                                <label htmlFor="password">Password</label>
                                <div className="tcl-control">
                                    <span className="tcl-lead">
                                        <Icon><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></Icon>
                                    </span>
                                    <input id="password" type={showPw ? "text" : "password"} value={password}
                                           onChange={(e) => setPassword(e.target.value)}
                                           placeholder="••••••••••" autoComplete="current-password" required />
                                    <button type="button" className="tcl-eye"
                                            onClick={() => setShowPw((s) => !s)}
                                            aria-pressed={showPw}
                                            aria-label={showPw ? "Hide password" : "Show password"}>
                                        {showPw
                                            ? <Icon><path d="M9.9 4.24A9.1 9.1 0 0 1 12 4c6.5 0 10 7 10 7a13.2 13.2 0 0 1-2.16 2.92M6.06 6.06A13.3 13.3 0 0 0 2 11s3.5 7 10 7a9.1 9.1 0 0 0 4.94-1.06M9.9 9.9a3 3 0 1 0 4.2 4.2" /><path d="M2 2l20 20" /></Icon>
                                            : <Icon><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></Icon>}
                                    </button>
                                </div>
                            </div>

                            <div className="tcl-forgot">
                                <Link to="/forgot-password">Forgot Password?</Link>
                            </div>

                            {SHOW_DEMO && (
                                <div className="tcl-field">
                                    <label htmlFor="demo-account">Sign in as (local demo)</label>
                                    <div className="tcl-control">
                                        <span className="tcl-lead">
                                            <Icon><circle cx="12" cy="12" r="9" /><path d="M3 12h18" /><path d="M12 3a15 15 0 0 1 0 18 15 15 0 0 1 0-18z" /></Icon>
                                        </span>
                                        <select id="demo-account" className="tcl-select" value={selected}
                                                onChange={(e) => pick(e.target.value)} aria-label="Sign in as">
                                            {DEMO_ACCOUNTS.map((a) => (
                                                <option key={a.email} value={a.email}>{a.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            )}

                            <button className="tcl-btn" type="submit" disabled={busy}>
                                <Icon><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><path d="M10 17l5-5-5-5" /><path d="M15 12H3" /></Icon>
                                {busy ? "Signing in…" : "Sign In"}
                            </button>
                        </form>

                        {SHOW_DEMO && (
                            <p className="tcl-demo-note">
                                Local demo — the selector fills that account's credentials (shared password).
                                Each role sees different data; scoping is enforced by the database, not the menu.
                            </p>
                        )}

                        <p className="tcl-support">
                            Trouble signing in?{" "}
                            <button type="button" className="tcl-link-btn" ref={openBtnRef}
                                    onClick={() => setSupportOpen(true)}>Contact support</button>
                        </p>

                        <div className="tcl-powered">
                            <img src="/brand/icon.png" alt="" aria-hidden="true" />
                            <span>Powered by TrustCare</span>
                        </div>
                    </div>
                </div>

                <p className="tcl-copyright">
                    © {branding.orgName} · <a href={PRIVACY_URL} target="_blank" rel="noopener">Privacy</a>
                </p>
            </main>

            {/* Contact support modal */}
            {supportOpen && (
                <div className="tcl-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setSupportOpen(false); }}>
                    <div className="tcl-modal" role="dialog" aria-modal="true" aria-labelledby="cs-title" ref={modalRef}>
                        <button type="button" className="tcl-modal-close" onClick={() => setSupportOpen(false)} aria-label="Close">
                            <Icon><path d="M18 6 6 18M6 6l12 12" /></Icon>
                        </button>
                        <span className="tcl-eyebrow">Support</span>
                        <h2 id="cs-title" className="tcl-modal-title">Contact support</h2>
                        <p className="tcl-modal-desc">Tell us what's happening and our IT team will get back to you. Leave your email so we can reply.</p>

                        <form className="tcl-form" onSubmit={sendSupport} noValidate>
                            <div className="tcl-field">
                                <label htmlFor="cs-email">Your email</label>
                                <div className="tcl-control">
                                    <span className="tcl-lead">
                                        <Icon><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></Icon>
                                    </span>
                                    <input id="cs-email" ref={csEmailRef} type="email" value={csEmail}
                                           onChange={(e) => setCsEmail(e.target.value)}
                                           placeholder="you@trustcare.com.au" autoComplete="email" required />
                                </div>
                            </div>
                            <div className="tcl-field">
                                <label htmlFor="cs-msg">What's going wrong?</label>
                                <textarea id="cs-msg" className="tcl-textarea" value={csMsg} rows={4} required
                                          onChange={(e) => setCsMsg(e.target.value)}
                                          placeholder="Describe the issue — e.g. password reset not arriving, account locked, error on sign in…" />
                            </div>
                            <button type="submit" className="tcl-btn">
                                <Icon><path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" /></Icon>
                                Send to IT support
                            </button>
                        </form>

                        {/* Phone/hours always shown; uses branding when set, else TrustCare's real values. */}
                        <p className="tcl-modal-alt">
                            Prefer to call?{" "}
                            <a href={`tel:${(branding.supportPhone ?? "0404 293 197").replace(/\s+/g, "")}`}>
                                {branding.supportPhone ?? "0404 293 197"}
                            </a>{" · "}
                            {branding.supportHours ?? "Monday to Friday, 08:30am–5:30pm (except public holidays)"}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};
